/**
 * Navigation pending state.
 *
 * react-router v7 navigates inside startTransition, so while a route chunk is
 * loading React keeps the previous page mounted and renders no Suspense
 * fallback. Correct behaviour — nothing flashes — but the screen looks frozen,
 * which is what made people click repeatedly.
 *
 * useNavigation() would tell us this, but it needs a data router
 * (createBrowserRouter) and this app uses <BrowserRouter> + <Routes>. So the
 * click is observed directly in ./prefetch.ts and reported here.
 */

type Listener = () => void;

let pending = false;
let token = 0;
const listeners = new Set<Listener>();

function emit(): void {
  listeners.forEach((l) => l());
}

export function subscribeNavProgress(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isNavPending(): boolean {
  return pending;
}

/**
 * Marks navigation as pending until `settled` resolves.
 *
 * Returns a cancel function. Later calls supersede earlier ones, so a fast
 * second navigation cannot be un-pended by the first one finishing.
 */
export function beginNavProgress(settled: Promise<unknown>): () => void {
  const mine = ++token;

  if (!pending) {
    pending = true;
    emit();
  }

  const finish = () => {
    if (mine !== token) return; // superseded
    pending = false;
    emit();
  };

  settled.then(finish, finish);

  return finish;
}

/** Called on committed location change, in case a promise never settles. */
export function endNavProgress(): void {
  token++;
  if (!pending) return;
  pending = false;
  emit();
}
